/**
 * `/api/analytics/*` (Phase 2A), one fetcher per contract endpoint, plus the probe that decides where
 * a page's numbers come from: the analytics API, the interim legacy queries, or nowhere.
 */
import { queryOptions } from '@tanstack/react-query';
import { isApiError } from '@/api/errors';
import { download, http } from '@/api/http';
import { filterParams, type ResolvedFilters, type Window } from '../filters';
import {
  queryResultSchema,
  statusSchema,
  type AnalyticsStatus,
  type DimensionId,
  type MetricId,
  type QueryRow,
} from './schemas';

export type AnalyticsSource = { kind: 'api'; status: AnalyticsStatus } | { kind: 'legacy' } | { kind: 'unavailable' };

/**
 * 200 → the analytics API. 404 → it isn't deployed here yet, so the Overview falls back to the old
 * dashboard queries. 503 `analytics_unavailable` → BigQuery is off in this environment.
 */
export const sourceQuery = queryOptions({
  queryKey: ['analytics', 'source'] as const,
  queryFn: async ({ signal }): Promise<AnalyticsSource> => {
    try {
      const status = await http('/analytics/status', { signal }, statusSchema);
      return status.available ? { kind: 'api', status } : { kind: 'unavailable' };
    } catch (error) {
      if (isApiError(error) && error.code === 'not_found') return { kind: 'legacy' };
      if (isApiError(error) && error.code === 'analytics_unavailable') return { kind: 'unavailable' };
      throw error;
    }
  },
  staleTime: 5 * 60_000,
});

export interface QuerySpec {
  metrics: MetricId[];
  dimensions?: DimensionId[];
  sort?: string;
  limit?: number;
  other?: boolean;
  /** Overrides the filters' window (e.g. the compare window for per-row deltas). */
  window?: Window;
  /** Send the filters' compare setting (totals only, per the contract). */
  withCompare?: boolean;
}

export function queryParams(f: ResolvedFilters, spec: QuerySpec) {
  return {
    ...filterParams(f, spec.window ?? f),
    metrics: spec.metrics.join(','),
    ...(spec.dimensions?.length ? { dimensions: spec.dimensions.join(',') } : {}),
    ...(spec.sort ? { sort: spec.sort } : {}),
    ...(spec.limit ? { limit: spec.limit } : {}),
    ...(spec.other ? { other: 'true' } : {}),
    compare: spec.withCompare ? f.compare : 'none',
  };
}

/**
 * Time-bucketed rows in time order, without the buckets still ahead: the API fills every bucket of
 * the window, so "today" by the hour includes the hours to come as zeros.
 */
export function pastBuckets<T extends QueryRow>(rows: T[], time: string, now = Date.now()): T[] {
  return rows
    .filter((r) => Date.parse(String(r[time])) <= now)
    .sort((a, b) => String(a[time]).localeCompare(String(b[time])));
}

/** `GET /api/analytics/query` as a plain call, for hooks that combine several specs in one fetch. */
export function fetchQuery(f: ResolvedFilters, spec: QuerySpec, signal?: AbortSignal) {
  return http('/analytics/query', { query: queryParams(f, spec), signal }, queryResultSchema);
}

/** XLSX or CSV of any query spec (`GET /api/analytics/export`), saved by the browser. */
export async function exportQuery(f: ResolvedFilters, spec: QuerySpec, format: 'xlsx' | 'csv') {
  const { blob, filename } = await download('/analytics/export', { query: { ...queryParams(f, spec), format } });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename ?? `analytics-${f.from}-${f.to}.${format}`;
  link.click();
  URL.revokeObjectURL(url);
}
